import { useCallback } from "react";
import { useCompanySettingsContext } from "../../context/CompanySettings/CompanySettingsContext";
import api from "../../services/api";

/**
 * Hook wrapper para useCompanySettingsContext
 *
 * @deprecated Este hook é mantido para compatibilidade com código legado.
 * Para novos componentes, use useCompanySettingsContext diretamente.
 *
 * Este hook agora utiliza o CompanySettingsContext internamente,
 * garantindo que todos os componentes compartilhem o mesmo cache
 * e evitando requisições redundantes à API.
 */
const useCompanySettings = () => {
    const { settings, getSetting, updateSetting, reloadSettings } = useCompanySettingsContext();

    /**
     * Obtém todas as configurações da empresa
     * Agora usa o cache do contexto ao invés de fazer requisição direta
     */
    const getAll = useCallback(async (companyId) => {
        console.log('⚠️ useCompanySettings.getAll() está deprecated. Use useCompanySettingsContext()');

        // Se os settings já estão carregados no contexto, retornar eles
        if (Object.keys(settings).length > 0) {
            return settings;
        }

        // Caso contrário, recarregar do servidor
        await reloadSettings();
        return settings;
    }, [settings, reloadSettings]);

    /**
     * Obtém uma configuração específica
     * Agora usa o cache do contexto
     */
    const get = useCallback(async (params) => {
        const column = typeof params === 'string' ? params : params?.column;

        if (!column) {
            console.error('❌ Parâmetro "column" é obrigatório para get()');
            throw new Error('Parâmetro "column" é obrigatório');
        }

        console.log(`⚠️ useCompanySettings.get("${column}") está deprecated. Use useCompanySettingsContext().getSetting("${column}")`);

        // Buscar do cache do contexto
        const value = getSetting(column);

        // Retornar no mesmo formato que a API antiga retornava
        // WhatsappModal espera { scheduleType: "connection" } e não { value: "connection", column: "scheduleType" }
        return { [column]: value, value, column };
    }, [getSetting]);

    /**
     * Atualiza uma configuração
     * Atualiza tanto no backend quanto no cache do contexto
     */
    const update = useCallback(async (data) => {
        console.log('⚠️ useCompanySettings.update() está deprecated. Use useCompanySettingsContext().updateSetting() + API');

        // Fazer a requisição para o backend
        const { data: responseData } = await api.put('/companySettings', data);

        // Atualizar o cache local
        if (data.column && data.data !== undefined) {
            updateSetting(data.column, data.data);
        }

        return responseData;
    }, [updateSetting]);

    return {
        getAll,
        get,
        update
    }
}

export default useCompanySettings;